<script setup lang="ts">
  import { ref, computed, watch, onMounted, nextTick } from 'vue'
  import SelectField, { type Option } from '@/components/admin/form/SelectField.vue'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import type { CustomerCase as Case, CustomerUser as User } from '@/types/generated'
  import { CasesService } from '@/types/generated/services.gen'
  import { useTranslation } from '@/composables/useTranslation'
  import { useToast } from '@/components/ui/toast'
  import { useLawFieldStore } from '@/store/lawFieldStore'
  import { useUserStore } from '@/store/userStore'
  import CaseTypeBadge from '@/components/app/cases/CaseTypeBadge.vue'
  import { Copy, ChevronDown } from 'lucide-vue-next'
  import Button from '@/components/ui/button/Button.vue'
  import ClerkSelectionPopover from '@/components/app/cases/ClerkSelectionPopover.vue'
  import type { ClerkOption } from '@/components/app/cases/ClerkSelectionPopover.vue'
  import { getInitials } from '@/lib/utils'

  const { t } = useTranslation({
    en: {
      inquiryData: 'Inquiry Data',
      caseWorker: 'Case Worker',
      legalField: 'Legal Field',
      legalTopic: 'Legal Topic',
      inquiryEmailAddress: 'Inquiry E-Mail Address',
      copy: 'Copy',
      createdBy: 'Created by',
      createdAt: 'on {date} at {time}',
      notSelected: 'Not selected',
      allLegalFields: 'All Legal Fields',
      allLegalTopics: 'All Legal Topics',
      allCaseWorkers: 'All Case Workers'
    },
    de: {
      inquiryData: 'Anfrage Daten',
      caseWorker: 'Sachbearbeiter',
      legalField: 'Rechtsgebiet',
      legalTopic: 'Rechtsthema',
      inquiryEmailAddress: 'Anfrage E-Mail Adresse',
      copy: 'Kopieren',
      createdBy: 'Erstellt von',
      createdAt: 'am {date} um {time} Uhr',
      notSelected: 'Nicht ausgewählt',
      allLegalFields: 'Alle Rechtsgebiete',
      allLegalTopics: 'Alle Rechtsthemen',
      allCaseWorkers: 'Alle Sachbearbeiter'
    }
  })

  // Format date and time for German locale
  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return ''
    const date = new Date(dateString)
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' })
  }

  const formatTime = (dateString: string | undefined): string => {
    if (!dateString) return ''
    const date = new Date(dateString)
    return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
  }

  // Store setup
  const caseStore = useCaseStore()
  const userStore = useUserStore()
  const caseRecord = computed<Case | null>(() => caseStore.caseRecord)

  // Options for select fields
  const legalFieldOptions = ref([])
  const legalTopicOptions = ref([])

  // Selected values for select fields
  const selectedLegalField = ref('')
  const selectedCaseWorkers = ref<string[]>([])
  const selectedLegalTopic = ref('')

  // Clerk selection
  const clerkFieldRef = ref<HTMLElement | null>(null)
  const clerkFieldWidth = ref('100%')

  const orgClerkOptions = computed<ClerkOption[]>(() => {
    return (userStore.user?.org?.users || [])
      .map((user: any) => ({
        uuid: user.uuid,
        firstName: user.first_name,
        lastName: user.last_name,
        avatar: user.avatar
      }))
      .sort((a: ClerkOption, b: ClerkOption) =>
        `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`, 'de')
      )
  })

  const selectedClerkDisplayData = computed(() => {
    return selectedCaseWorkers.value
      .map((uuid) => {
        const clerk = orgClerkOptions.value.find((c) => c.uuid === uuid)
        if (!clerk) return null
        return {
          uuid: clerk.uuid,
          fullName: `${clerk.firstName} ${clerk.lastName}`
        }
      })
      .filter(Boolean)
  })

  const updateClerkFieldWidth = () => {
    if (clerkFieldRef.value) {
      clerkFieldWidth.value = `${clerkFieldRef.value.offsetWidth}px`
    }
  }

  const { toast } = useToast()

  const user = computed<User>(() => useUserStore().user as User)
  const { fetchLawFields, getLawTopicOptions, getLawFieldOptions } = useLawFieldStore()
  const lawFields = computed(() => useLawFieldStore().lawFields)
  const lawFieldOptions = computed<Option[]>(() => {
    return getLawFieldOptions(user.value)
  })
  const lawTopicOptions = computed<Option[]>(() => {
    return getLawTopicOptions(selectedLegalField.value)
  })
  // const lawTopicOptions = computed<Option[]>(() => {
  //   return getLawTopicOptions(createData.value.law.field)
  // })
  // Email address
  // const emailAddress = computed(() => caseRecord.value?.data?.contact?.email || '')

  // Watch for changes in caseRecord and update options
  watch(
    () => userStore.user?.org,
    (org) => {
      if (org) {
        legalFieldOptions.value = (org.law_fields || []).map((field) => ({
          value: field,
          title: field
        }))

        // Assuming legal topics are available in org or could be derived from legal field
        legalTopicOptions.value = (org.legal_topics || []).map((topic) => ({
          value: topic,
          title: topic
        }))
      }
    },
    { immediate: true }
  )

  watch(
    () => caseRecord.value?.data?.law?.field,
    (field) => {
      selectedLegalField.value = field || ''
    },
    { immediate: true }
  )

  watch(
    () => caseRecord.value?.data?.law?.topic,
    (topic) => {
      selectedLegalTopic.value = topic || ''
    },
    { immediate: true }
  )

  watch(
    () => (caseRecord.value as any)?.clerks,
    (clerks) => {
      selectedCaseWorkers.value = (clerks || []).map((c: any) => c.uuid)
    },
    { immediate: true }
  )

  const handleFilterChange = async (value: any, fieldName: string) => {
    if (!caseRecord.value?.uuid) return

    try {
      let requestBody: Record<string, unknown> = {}

      if (fieldName === 'legalfield') {
        requestBody = {
          data: {
            law: {
              field: value
            }
          }
        }
      } else if (fieldName === 'legaltopic') {
        requestBody = {
          data: {
            law: {
              topic: value
            }
          }
        }
      } else if (fieldName === 'caseworker') {
        // Handle case worker assignment (multi-clerk)
        requestBody = {
          clerk_ids: value || []
        }
      }

      await CasesService.casesPartialUpdate({
        uuid: caseRecord.value.uuid,
        requestBody
      })
    } catch (error) {
      toast({ title: 'Error', variant: 'destructive' })
    }
  }

  const emailAddress = userStore.user?.org?.inbound_email_address

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(emailAddress || '')
      toast({
        title: 'Erfolg',
        description: 'E-Mail Adresse wurde in die Zwischenablage kopiert',
        variant: 'default'
      })
    } catch (error) {
      toast({
        title: 'Fehler',
        description: 'E-Mail Adresse konnte nicht kopiert werden',
        variant: 'destructive'
      })
    }
  }

  const handleClerkConfirm = async (clerkIds: string[]) => {
    selectedCaseWorkers.value = clerkIds
    await handleFilterChange(clerkIds, 'caseworker')
  }

  // TODO: Fix the async
  onMounted(async () => {
    await fetchLawFields()
    await nextTick()
    updateClerkFieldWidth()
  })
</script>

<template>
  <div class="inquiry-data-widget flex flex-col gap-2">
    <div>
      <h3 class="pb-1">{{ t('createdBy') }}</h3>
      <div class="flex items-center gap-2">
        <CaseTypeBadge
          v-if="caseRecord?.type"
          :type="caseRecord.type"
        />
        <span>
          {{
            t('createdAt', {
              date: formatDate(caseRecord?.created_at),
              time: formatTime(caseRecord?.created_at)
            })
          }}
        </span>
      </div>
    </div>
    <div class="flex items-start gap-2">
      <!-- Legal Field -->
      <SelectField
        v-model="selectedLegalField"
        :options="lawFieldOptions"
        :placeholder="t('legalField')"
        :allow-unselect="true"
        :unselected-option-text="t('notSelected')"
        class="grow"
        name="legalfield"
        :label="t('legalField')"
        @update:model-value="(value) => handleFilterChange(value, 'legalfield')"
      />
      <!-- Legal Topic -->
      <SelectField
        v-model="selectedLegalTopic"
        :options="lawTopicOptions"
        :placeholder="t('legalTopic')"
        :allow-unselect="true"
        :unselected-option-text="t('notSelected')"
        class="grow"
        name="legaltopic"
        :label="t('legalTopic')"
        @update:model-value="(value) => handleFilterChange(value, 'legaltopic')"
      />
      <!-- Clerk -->
      <div
        ref="clerkFieldRef"
        class="grow"
      >
        <label class="mb-1 block text-sm font-medium text-gray-700">
          {{ t('caseWorker') }}
        </label>
        <ClerkSelectionPopover
          :clerk-options="orgClerkOptions"
          :current-clerk-ids="selectedCaseWorkers"
          :max-clerks="3"
          :popover-width="clerkFieldWidth"
          @confirm="handleClerkConfirm"
        >
          <div
            class="flex h-[40px] w-full cursor-pointer items-center gap-2 rounded-lg border border-gray-200 bg-white
              px-2 text-sm"
            @click="updateClerkFieldWidth"
          >
            <template v-if="selectedClerkDisplayData.length">
              <!-- Overlapping avatar circles -->
              <div class="flex -space-x-1.5">
                <div
                  v-for="clerk in selectedClerkDisplayData"
                  :key="clerk.uuid"
                  class="flex h-6 w-6 items-center justify-center rounded-full bg-purple-500 text-[10px] font-medium
                    text-white ring-2 ring-white"
                >
                  {{ getInitials(clerk.fullName) }}
                </div>
              </div>
              <!-- First clerk name -->
              <span class="min-w-0 truncate text-gray-900">
                {{ selectedClerkDisplayData[0].fullName }}
              </span>
            </template>
            <span
              v-else
              class="text-gray-500"
            >
              {{ t('notSelected') }}
            </span>
            <ChevronDown class="ml-auto h-4 w-4 shrink-0 text-gray-400" />
          </div>
        </ClerkSelectionPopover>
      </div>
    </div>

    <!-- Email Address -->
    <div>
      <label class="mb-0 block text-sm font-medium text-gray-700">
        {{ t('inquiryEmailAddress') }}
      </label>
      <div class="flex gap-2">
        <div
          class="flex min-w-0 flex-grow items-center rounded-md border border-gray-200 bg-white px-3 py-2
            text-gray-900"
        >
          <span class="truncate">{{ emailAddress }}</span>
        </div>
        <Button
          type="button"
          variant="secondary"
          size="default"
          class="gap-2"
          @click="copyToClipboard"
        >
          <Copy class="h-5 w-5 text-gray-800" />
          {{ t('copy') }}
        </Button>
      </div>
    </div>
  </div>
</template>
